<template>
  <VDialogV3
    id="budget-price-card"
    position="right"

    width="600px"

    :entity="localBudgetPrice"
    :validate-on-mount="true"
    :submit-disabled="submitDisabled"
    :submit-method="submit"
  >
    <section v-if="localBudgetPrice !== null">
      <VSuggestionSelectV2
        v-model="localBudgetPrice.product"
        :data="({ pattern }) => ProductsService.search(pattern)"
        label="Продукт"
        placeholder="Введите наименование продукта"
        layout="vertical"
        required
        :disabled="product !== null"
      >
        <template #helper-text>
          Артикул:

          <template v-if="localBudgetPrice.product?.code">
            {{ localBudgetPrice.product.code }}
          </template>

          <template v-else>
            отсутствует
          </template>

        </template>
      </VSuggestionSelectV2>

      <VFormItem
        label="Бюджетная цена"
        layout="vertical"
        required
        :unit="`за ${localBudgetPrice.product?.unit?.name ?? '--'}`"
      >
        <VInputNumberRusV2
          v-model="localBudgetPrice.price"
          :precision="3"
          :type="Price"
          required
        />

        <VSelectV2
          v-model="localBudgetPrice.currency"
          :data="$core.currencies.currencies"
          option-identity="code"
          required
        />
      </VFormItem>

      <VInputNumberRusV2
        v-model="localBudgetPrice.optimalPurchaseQty"
        label="Оптимальный объём закупки СЗ"
        layout="vertical"
        :unit="localBudgetPrice.product?.unit?.name"
        :type="Qty"
      />

      <VDateInput
        v-model="localBudgetPrice.validityPeriod.start"
        label="Дата начала действия"
        layout="vertical"
        required
      />
    </section>

    <hr v-if="localBudgetPrice !== null">

    <section v-if="localBudgetPrice !== null">
      <VInputNumberRusV2
        :model-value="localBudgetPrice.product?.productionRequiredPackageVolume"
        label="Фасовка (требование производства)"
        layout="vertical"
        :type="Qty"
        :unit="localBudgetPrice.product?.unit?.name"
        disabled
        :disabled-reasons="['Берётся из номенклатуры, меняется в её карточке']"
      />
    </section>
  </VDialogV3>
</template>

<script lang="ts">
import BudgetPrice from "@/domains/BudgetPrices/BudgetPrice";
import BudgetPricesService from "@/domains/BudgetPrices/BudgetPricesService";
import ProductsService from "@/domains/Products/ProductsService";
import type Product from "@/domains/Products/Product";
import Price from "@/core/types/Price";
import Qty from "@/core/types/Qty";

/**
 * Карточка новой записи бюджетной цены. Записи не редактируются: изменение цены или объёма — новая запись,
 * поэтому карточка всегда создаёт запись, а условия копирует из действующей цены номенклатуры
 */
export default defineComponent({
  name: "BudgetPriceCard",

  props: {
    /** Номенклатура, для которой заводится цена. null — номенклатуру выбирают в карточке */
    product: {
      type: Object as PropType<Product | null>,
      default: null,
    },
  },

  setup() {
    return {
      ProductsService,
      Price,
      Qty,
    }
  },

  data() {
    return {
      localBudgetPrice: null as unknown as BudgetPrice,
    }
  },

  async mounted() {
    await this.init();
  },

  computed: {
    submitDisabled(): boolean {
      return this.$core.utils.isNullOrUndefined(this.localBudgetPrice?.product?.id) === true
        || this.$core.utils.isNullOrUndefined(this.localBudgetPrice?.price) === true
        || this.$core.utils.isNullOrUndefined(this.localBudgetPrice?.currency?.code) === true;
    }
  },

  methods: {
    async init() {
      if (this.product === null) {
        this.localBudgetPrice = BudgetPrice.fromNew();
      }
      else {
        const actual = this.product.actualBudgetPrice ?? await BudgetPricesService.get(this.product.id);

        if (actual !== null) {
          this.localBudgetPrice = BudgetPrice.fromPrevious(actual);
        }
        else {
          this.localBudgetPrice = BudgetPrice.fromNew();
          this.localBudgetPrice.product = this.product;
        }
      }
    },

    /** Сабмит через submitMethod: валидацию формы и resolve() делает VDialogV3 */
    async submit(): Promise<boolean> {
      return await BudgetPricesService.add(this.localBudgetPrice);
    },
  },
})
</script>
